import { useRef, useState, useMemo, Children } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import { Theme, useMediaQuery } from '@mui/material';
import { useTheme } from '@classify/react-core-components/styles';

import Box from '@classify/react-core-components/Box';
import ClassifyCarousel from '@classify/react-core-components/Carousel';
import Typography from '@classify/react-core-components/Typography';
import { ChevronRight, ChevronLeft } from '@mui/icons-material';
import { styled } from '@classify/react-core-components/styles';

import type { Breakpoint } from './types/Breakpoint';

export const defaultTranslations = {
  previousLabel: 'Go back',
  nextLabel: 'Go to next slide',
};

export interface Props {
  title?: ReactNode;
  children?: ReactNode;
  sliderProps?: {
    previousArrowProps?: HTMLAttributes<HTMLButtonElement>;
    nextArrowProps?: HTMLAttributes<HTMLButtonElement>;
  };
  translations?: { previousLabel: string; nextLabel: string };
  slidesPerPage?: number;
  slidesWidth?: Extract<Breakpoint, 'sm' | 'md' | 'lg'>;
  shouldHideNavigation?: boolean;
  shouldDisableCardFading?: boolean;
  onSlideChange?: (currentSlide: number) => void;
}

const CarouselHeader = styled('div')(({ theme: { spacing, breakpoints } }) => ({
  display: 'flex',
  gap: spacing(2),
  alignItems: 'flex-end',
  justifyContent: 'flex-end',
  marginBottom: spacing(2),
  [breakpoints.up('md')]: {
    alignItems: 'center',
  },
}));
const CarouselNav = styled('div')(({ theme: { spacing } }) => ({
  display: 'flex',
  gap: spacing(2),
  alignItems: 'center',
}));
const CarouselArrow = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'none',
  width: 32,
  height: 32,
  borderRadius: '100%',
  border: '1px solid',
  cursor: 'pointer',
  '&:disabled': {
    cursor: 'auto',
    opacity: 0.5,
  },
});
const CarouselContainer = styled('div', {
  shouldForwardProp: (prop) => !['slidesWidth', 'shouldDisableCardFading'].includes(prop as string),
})<{
  theme?: Theme;
  slidesWidth: Props['slidesWidth'];
  shouldDisableCardFading?: boolean;
}>(
  /* istanbul ignore next */
  ({ theme: { spacing, breakpoints }, slidesWidth, shouldDisableCardFading }) => ({
    overflow: 'hidden',
    '&': {
      margin: spacing(0, -0.5),
      [breakpoints.up('md')]: {
        margin: spacing(0, -1),
      },
    },
    '& .slick-list': {
      overflow: 'visible',
      paddingBottom: spacing(2),
      width: '90%',
      ...(slidesWidth === 'sm' && {
        width: 280,
        [breakpoints.up('sm')]: {
          width: 520,
        },
        [breakpoints.up('md')]: {
          width: 684,
        },
      }),
      ...(slidesWidth === 'md' && {
        width: 400,
        [breakpoints.up('sm')]: {
          width: 720,
        },
        [breakpoints.up('md')]: {
          width: 944,
        },
      }),
    },
    '& .slick-track': {
      display: 'flex',
    },
    '& .slick-slide': {
      transition: 'opacity 0.3s ease-out',
      opacity: shouldDisableCardFading ? 1 : 0.5,
      height: 'inherit !important',
      [breakpoints.up('md')]: {
        height: 'unset',
      },
    },
    '& .slick-slide.slick-active': {
      opacity: 1,
    },

    '& .slick-slide > div': {
      height: '100%',
      padding: spacing(0, 0.5),
      [breakpoints.up('md')]: {
        padding: spacing(0, 1),
      },
    },
    '& .slick-slide > div > :first-of-type': {
      height: '100%',
    },
  })
);

const CardCarousel = ({
  title,
  children = null,
  translations = {
    previousLabel: defaultTranslations.previousLabel,
    nextLabel: defaultTranslations.nextLabel,
  },
  sliderProps = {},
  slidesPerPage = 1,
  slidesWidth = 'lg',
  shouldHideNavigation,
  shouldDisableCardFading,
  onSlideChange,
}: Props) => {
  const [currentPage, setCurrentPage] = useState(1);
  const cardCarouselSlider = useRef<ClassifyCarousel>(null);
  const { breakpoints } = useTheme();
  const shouldDisplayArrows = useMediaQuery(breakpoints.up('sm'));

  const cardCount = Children.toArray(children).length;
  const totalPages = useMemo(
    () => Math.ceil(cardCount / slidesPerPage),
    [slidesPerPage, cardCount]
  );

  const hasOnlyOneChild = cardCount === 1;

  if (!children) {
    return null;
  }

  const slickSettings = {
    infinite: false,
    slidesToShow: slidesPerPage,
    slidesToScroll: slidesPerPage,
    arrows: false,
    swipe: true,
    beforeChange: /* istanbul ignore next */ (oldIndex: number, newIndex: number) => {
      const hasNextPage = currentPage < totalPages || newIndex < oldIndex;
      const hasPreviousPage = currentPage > 1 || newIndex > oldIndex;

      if (!hasNextPage || !hasPreviousPage) return;

      const currentPageIndex = newIndex >= oldIndex ? currentPage + 1 : currentPage - 1;
      setCurrentPage(currentPageIndex);
      onSlideChange?.(currentPageIndex);
    },
  };
  /* istanbul ignore next */
  const handlePrevNav = () => {
    cardCarouselSlider?.current?.slickPrev();
  };
  /* istanbul ignore next */
  const handleNextNav = () => {
    cardCarouselSlider?.current?.slickNext();
  };

  return (
    <Box data-testid="card-carousel-wrapper-item" sx={{ overflow: 'hidden' }}>
      <CarouselHeader>
        <Box sx={{ flexGrow: 1 }}>{title}</Box>
        {!hasOnlyOneChild && !shouldHideNavigation && (
          <CarouselNav>
            <Typography>
              {currentPage}/{totalPages}
            </Typography>
            {shouldDisplayArrows && (
              <>
                <CarouselArrow
                  disabled={currentPage === 1}
                  aria-label={translations.previousLabel}
                  onClick={handlePrevNav}
                  data-testid="card-carousel-previous"
                  {...sliderProps?.previousArrowProps}
                >
                  <ChevronLeft />
                </CarouselArrow>
                <CarouselArrow
                  disabled={currentPage >= totalPages}
                  aria-label={translations.nextLabel}
                  onClick={handleNextNav}
                  data-testid="card-carousel-next"
                  {...sliderProps?.nextArrowProps}
                >
                  <ChevronRight />
                </CarouselArrow>
              </>
            )}
          </CarouselNav>
        )}
      </CarouselHeader>
      {hasOnlyOneChild ? (
        children
      ) : (
        <Box data-testid="card-carousel-item">
          <CarouselContainer
            slidesWidth={slidesWidth}
            shouldDisableCardFading={shouldDisableCardFading}
          >
            <ClassifyCarousel {...slickSettings} ref={cardCarouselSlider}>
              {children}
            </ClassifyCarousel>
          </CarouselContainer>
        </Box>
      )}
    </Box>
  );
};
export default CardCarousel;
